import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { isCopyrightViolationError } from 'services/helpers/error';
import useMessageSnackbar from 'services/hooks/useMessageSnackbar';
import { locations } from 'services/routes/locations';
import useSubscription from '../subscription';
import useDownloadLicenseRequest from './useDownloadLicenseRequest';
import { appendDownloadLink } from './helpers';

interface UseDownloadLicense {
  songId: string;
  onSuccess: () => void;
}

const useDownloadLicense = ({ songId, onSuccess }: UseDownloadLicense) => {
  const { t } = useTranslation();

  const navigate = useNavigate();

  const [loading, response, error, downloadLicense] =
    useDownloadLicenseRequest();

  const { displayErrorSnackbar } = useMessageSnackbar();
  const { isFreeAccount } = useSubscription();

  const [isCopyrightViolationModalOpen, setIsCopyrightViolationModalOpen] =
    useState(false);

  useEffect(() => {
    if (!response) {
      return;
    }
    appendDownloadLink(response.url, response.filename);
    URL.revokeObjectURL(response.url);
    onSuccess();
    // eslint-disable-next-line
  }, [response]);

  useEffect(() => {
    if (!error) {
      return;
    }
    if (isCopyrightViolationError(error)) {
      setIsCopyrightViolationModalOpen(true);
      return;
    }
    displayErrorSnackbar(t('messages:error:unknown'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t, error]);

  const handleLicenseDownload = useCallback(() => {
    if (isFreeAccount) {
      navigate(locations.subscription.pricing.pathname(''));
      return;
    }

    downloadLicense(songId);
  }, [isFreeAccount, navigate, downloadLicense, songId]);

  return {
    loading,
    success: !!response,
    downloadLicense: handleLicenseDownload,
    isCopyrightViolationModalOpen,
    closeCopyrightViolationModal: () => setIsCopyrightViolationModalOpen(false),
  };
};

export default useDownloadLicense;
